import { Moment } from 'moment';
import { InternetContractType, InternetRouters, InternetTypes } from 'core/enums/internet';
import { VasCodesType } from 'core/enums/vas-code';
import { Util } from 'utils/common.utils';
import { Hardware } from './hardware';
import { HardwareDTO } from './hardware.dto';
import { Databalance } from './internet/internet';
import { QuoteDetails, StreamQuantity } from './link-package.dto';
import { PackagesDTO } from './packages.dto';
import { ChannelContent, ChannelGenreContent, FeaturedChannel } from './select.dto';
import { ServicesDTO } from './services.dto';
import { GetSubscriptionDTO, SubscriptionDTO } from './subscription.dto';
import { ApiActionDTO } from '../general/api-action.dto';

export class Product implements Partial<SubscriptionDTO>, Partial<ServicesDTO> {
  // NOTE: Umbraco (augmented) properties
  code!: string;
  description!: string;
  features!: string;
  iconCode!: string;
  hardwareIconCode!: string;
  packageColor!: string;
  listedPrice!: any;
  productCodes!: string;
  productDetail!: string;
  productCardImage!: string;
  productName!: string;
  productSummary!: string;
  productContent!: string;
  secondaryPackageColor!: string;
  mobileImage!: string;
  backgroundImage!: string;
  logoImage!: string;
  logoImageDark!: string;
  color!: string;
  content!: string;
  heading!: string;
  showFloatingLogo!: boolean;
  logo!: string;
  productImage!: string;
  packageName!: string;
  floatingLogoImage!: string;
  floatingLogoImageLight!: string;
  floatingLogoImageDark!: string;
  mobileBackgroundPattern!: string;
  backgroundPattern!: string;
  promoBackgroundImage?: string;
  priceLockSaving?: number;
  productCardCTA?: string;
  productDetailCTA?: string;
  // NOTE: From API properties
  serviceId?: number;
  serviceName?: string;
  serviceDescription!: string;
  status?:
    | 'Disconnected'
    | 'Active'
    | 'Request Disconnect'
    | 'Request Downgrade'
    | 'Suspended'
    | 'Disconnect Pending Reconnect'
    | 'Awaiting Payment'
    | 'Pending Upgrade'
    | 'Waiting Trial'
    | 'Pending Removal'
    | 'Instalment Active'
    | 'Instalment Pending';
  type?: 'Flex' | 'ThirdParty' | 'Bundle' | 'Product Installation' | 'Showmax' | 'Disney' | 'Access' | 'Principal' | 'Movies' | 'Netflix' | 'Addon' | 'BritBox' | 'Short Term Subscription' | 'SecurityLifestyle';
  psnr?: number;
  price!: number | null;
  chargePeriod?: number;
  lastDisconnectionDate?: string;
  quantities?: StreamQuantity[];
  // currency?: CurrencyCode;
  isExpired?: boolean;
  bundleType?: string;
  bundleId?: number;
  bundleStatus?: string;
  bundleDefinitionId?: string;
  bundleName?: string;
  bundleContractEndDate?: string;
  bundleContractStartDate?: string;
  bundleContractPeriod?: number;
  localPrice?: number;
  productId!: number;
  actions?: ApiActionDTO[];
  // NOTE: properties from parent subscription
  smartcardNumber!: string;
  smartcardName?: string;
  isPrimarySc!: boolean;
  isSecondarySc!: boolean;
  isXtraView!: boolean;
  viewingDaysLeft?: number;
  agreementId!: number;
  agreementType?: 'Employee' | 'OTT' | 'General' | 'Holiday Home' | 'Employee OTT';
  model?: string;
  modelDescription?: string;
  isInsured?: boolean;
  serialNumber?: string;
  // NOTE: composite properties
  addons!: Partial<Product>[];
  flexProducts!: Partial<Product>[];
  activeOverride = false;
  active = false;
  hasServices = false;
  parentId?: number;
  //NOTE: change package property
  changePackageType?: 'upgrade' | 'downgrade';
  insurancePrice?: string;
  insuranceLabel?: string;
  insurancePriceTerm?: string;
  coverAmount?: string;
  monthlyPrice?: string;
  ageGroup?: string;
  //NOTE: Internet product properties
  internetBundleDesc?: string;
  expireDate?: Moment;
  quantity?: string;
  routerType?: InternetRouters;
  internetType?: InternetTypes;
  simCardNumber?: string;
  simLocked?: boolean;
  descriptionIcon?: string;
  internetProvider?: string;
  dataPackage?: string;
  anyTimeDataBalance?: number;
  nightTimeDataBalance?: number;
  databalance?: Databalance[];
  internetContractType?: InternetContractType;
  //NOTE: Added for distinguishing subscription from hardware products in the carousel
  isHardware?: boolean;
  isHighestPackage?: string;
  discount?: number;
  originalAmount?: number;
  //Needed to display lineItems in the link addons
  lineItems?: QuoteDetails[];
  //Needed for carousel card top label to indicate whether the product awaits activation
  isActivationPending?: boolean;
  activationAction?: string;
  featuredChannels?: FeaturedChannel[];
  isPendingChangePackageSchedule?: boolean;
  scheduleId?: number;

  get uri() {
    if (this.type === `${VasCodesType.FLEX}`) {
      return '/subscription-details/select';
    }

    return `/subscription/${this.productId}/`;
  }

  get productType() {
    if (this.type === `${VasCodesType.FLEX}`) {
      return VasCodesType.FLEX;
    }

    return undefined;
  }

  get isActive() {
    return (
      this.status !== 'Disconnected' &&
      this.status !== 'Request Disconnect' &&
      // this.status !== 'Suspended' &&
      this.status !== 'Disconnect Pending Reconnect'
    );
  }

  get serviceCode() {
    const nameParts = this.serviceName?.split('-') || [];
    return nameParts.length ? nameParts[0].trim() : this.serviceName;
  }

  get hardwareInfo(): Hardware {
    return {
      model: this.model,
      modelDescription: this.modelDescription,
      productCardImage: this.productCardImage,
      isPrimarySc: this.isPrimarySc,
      isSecondarySc: this.isSecondarySc,
      isXtraView: this.isXtraView,
      smartcardNumber: this.smartcardNumber,
      isInsured: this.isInsured,
      agreementId: this.agreementId,
      productName: this.productName,
      serialNumber: this.serialNumber,
      agreementType: this.agreementType,
      hardwareIconCode: this.hardwareIconCode,
    };
  }

  constructor(dto?: Product) {
    if (dto) {
      this.code = dto.code;
      this.description = dto.description;
      this.serviceDescription = dto.serviceDescription;
      this.features = dto.features;
      this.iconCode = dto.iconCode;
      this.packageColor = dto.packageColor;
      this.price = dto.price;
      this.listedPrice = dto.listedPrice;
      this.productDetail = dto.productDetail;
      this.productCardImage = dto.productCardImage;
      this.productName = dto.productName;
      this.productSummary = dto.productSummary;
      this.productContent = dto.productContent;
      this.secondaryPackageColor = dto.secondaryPackageColor;
      this.serviceId = dto.serviceId || undefined;
      this.productId = dto.productId;
      this.serviceName = dto.serviceName || undefined;
      this.status = dto.status || undefined;
      this.type = dto.type || undefined;
      this.psnr = dto.psnr || undefined;
      this.chargePeriod = dto.chargePeriod || undefined;
      this.lastDisconnectionDate = dto.lastDisconnectionDate || undefined;
      // this.currency = dto.currency || undefined;
      this.isExpired = dto.isExpired || undefined;
      this.bundleType = dto.bundleType;
      this.bundleId = dto.bundleId;
      this.bundleStatus = dto.bundleStatus;
      this.bundleDefinitionId = dto.bundleDefinitionId;
      this.bundleName = dto.bundleName;
      this.bundleContractEndDate = dto.bundleContractEndDate;
      this.bundleContractStartDate = dto.bundleContractStartDate;
      this.bundleContractPeriod = dto.bundleContractPeriod;
      this.localPrice = dto.localPrice;
      this.smartcardNumber = dto.smartcardNumber;
      this.smartcardName = dto.smartcardName;
      this.serialNumber = dto.serialNumber;
      this.isPrimarySc = dto.isPrimarySc || false;
      this.isSecondarySc = dto.isSecondarySc || false;
      this.isXtraView = dto.isXtraView || false;
      this.isInsured = dto.isInsured || false;
      this.viewingDaysLeft = dto.viewingDaysLeft;
      this.agreementId = dto.agreementId;
      this.agreementType = dto.agreementType;
      this.model = dto.model;
      this.modelDescription = dto.modelDescription;
      this.addons = dto.addons || [];
      this.flexProducts = dto.flexProducts || [];
      this.parentId = dto.parentId;
      this.actions = dto.actions;
      this.expireDate = dto.expireDate;
      this.routerType = dto.routerType;
      this.internetType = dto.internetType;
      this.internetBundleDesc = dto.internetBundleDesc;
      this.internetProvider = dto.internetProvider;
      this.dataPackage = dto.dataPackage;
      this.anyTimeDataBalance = dto.anyTimeDataBalance;
      this.internetContractType = dto.internetContractType;
      this.productCardCTA = dto.productCardCTA;
      this.productDetailCTA = dto.productDetailCTA;
      this.scheduleId = dto.scheduleId;
      this.isPendingChangePackageSchedule = dto.isPendingChangePackageSchedule;
    }
  }

  static FromUmbracoPackageDTO(dto: PackagesDTO | Partial<HardwareDTO>, product: Product = new Product(), channels?: ChannelContent[], channelGenres?: ChannelGenreContent[]) {
    product.mobileImage = dto.SelfServiceMobileBackgroundImage
      ? Util.GetMediaPath(dto.SelfServiceMobileBackgroundImage)
      : '';
    product.productCardImage = dto.SelfServiceProductCardImage
      ? Util.GetMediaPath(dto.SelfServiceProductCardImage)
      : '';
    product.productImage = dto.ProductImage ? Util.GetMediaPath(dto.ProductImage) : '';
    product.floatingLogoImage = dto.SelfServiceLogoImage ? Util.GetMediaPath(dto.SelfServiceLogoImage) : '';
    product.floatingLogoImageDark = dto.SelfServiceLogoImageDark ? Util.GetMediaPath(dto.SelfServiceLogoImageDark) : '';
    product.floatingLogoImageLight = dto.SelfServiceLogoImageLight ? Util.GetMediaPath(dto.SelfServiceLogoImageLight) : '';
    product.backgroundImage = dto.SelfServiceBackgroundImage ? Util.GetMediaPath(dto.SelfServiceBackgroundImage) : '';
    product.color = dto.SelfServiceProductColor || dto.PackageColor || '';
    product.heading = dto.SelfServiceProductHeading || '';
    product.content = dto.SelfServiceProductContent || '';
    product.description = product.serviceName?.includes(VasCodesType.STREAMMOBILE) ? `Streaming on ${product.quantity} device` : dto.SelfServiceProductDescription || '';
    product.mobileBackgroundPattern = dto.SelfServiceMobileBackgroundPattern
      ? Util.GetMediaPath(dto.SelfServiceMobileBackgroundPattern)
      : '';
    product.backgroundPattern = dto.SelfServiceBackgroundPattern
      ? Util.GetMediaPath(dto.SelfServiceBackgroundPattern)
      : '';
    product.productCodes = dto.SelfServiceProductCodes || '';
    product.hardwareIconCode = dto.IconCode || '';
    product.code = dto.SelfServiceProductCodes || '';
    product.features = dto.Features || '';
    product.iconCode = dto.SelfServiceIconCode || '';
    product.listedPrice = dto.Price || null;
    product.priceLockSaving = dto.PriceLockSaving || undefined;
    product.productDetail = dto.ProductDetail || '';
    product.productName = dto.ProductName || '';
    product.price = this.getPrice(product, dto);
    product.productSummary = dto.SelfServiceProductSummary || dto.ProductSummary || '';
    product.productContent = dto.SelfServiceProductContent || '';
    product.secondaryPackageColor = dto.SecondaryPackageColor || '';
    product.insurancePrice = dto.SelfServiceInsurancePrice || (dto.Price ? `${dto.Price}` : undefined);
    product.insuranceLabel = dto.SelfServiceInsuranceLabel || '';
    product.insurancePriceTerm = dto.SelfServiceInsurancePriceTerm || '';
    product.promoBackgroundImage = dto.SelfServicePromoBackgroundImage || '';
    product.productCardCTA = dto.SelfServiceProductCardCTA || '';
    product.productDetailCTA = dto.SelfServiceProductDetailCTA || '';
    product.logo = Util.GetMediaPath(dto.Logo || dto.SelfServiceLogoImage || '') || '';
    product.coverAmount = dto.SelfServiceCoverAmount || '';
    product.monthlyPrice = dto.SelfServiceMonthlyPrice || '';
    product.ageGroup = dto.SelfServiceAgeGroup || '';
    product.featuredChannels = this.mapChannelsToFeaturedChannels(channels, channelGenres, dto.FeaturedChannels)
    return product;
  }

  static mapChannelsToFeaturedChannels(channels?: ChannelContent[], channelGenres?: ChannelGenreContent[], featuredChannels?: number[]): FeaturedChannel[] | undefined {
    return featuredChannels?.map((channelId: number) => {
      const matchingContent = channels?.find((item: ChannelContent) => item.Id === `${channelId}`);
      return {
        channelLogo: matchingContent?.ChannelLogo,
        channelName: matchingContent?.ChannelName,
        genre: this.mapGenresToFeaturedChannels(channelGenres, matchingContent?.ChannelGenre)
      } as FeaturedChannel;
    })
  }

  static getPrice(product: Product, dto: PackagesDTO | Partial<HardwareDTO>) {
    if (product.bundleDefinitionId && product.agreementType === 'OTT' && dto.SelfServiceMonthlyPrice) {
      return Number(dto.SelfServiceMonthlyPrice);
    }

    return product.price;
  }

  static mapGenresToFeaturedChannels(channelGenre?: ChannelGenreContent[], genres?: number[]): string | undefined {
    return genres?.reduce((genre: string, genreId: number) => {
      const matchingContent = channelGenre?.find((itm: ChannelGenreContent) => itm.Id === `${genreId}`);
      return genre.length > 0 ? genre + `/ ${matchingContent?.GenreName}` : genre + matchingContent?.GenreName;
    }, '')
  }

  static FromSubscriptionDTO(
    dto: Partial<SubscriptionDTO & ServicesDTO & { flexProducts: ServicesDTO[]; addons: ServicesDTO[]; agreementId: number }>,
    product: Product
  ) {
    product.actions = dto.actions;
    product.serviceId = dto.serviceId;
    product.serviceName = dto.serviceName;
    product.serialNumber = dto.serialNumber;
    product.serviceDescription = dto.description || '';
    product.status = dto.status;
    product.type = dto.type;
    product.psnr = dto.psnr;
    product.price = dto.price || null;
    product.chargePeriod = dto.chargePeriod;
    product.lastDisconnectionDate = dto.lastDisconnectionDate;
    // product.currency = dto.currency;
    product.isExpired = dto.isExpired;
    product.bundleType = dto.bundleType;
    product.bundleId = dto.bundleId;
    product.bundleStatus = dto.bundleStatus;
    product.bundleDefinitionId = dto.bundleDefinitionId;
    product.bundleName = dto.bundleName;
    product.bundleContractEndDate = dto.bundleContractEndDate;
    product.bundleContractStartDate = dto.bundleContractStartDate;
    product.bundleContractPeriod = dto.bundleContractPeriod;
    product.localPrice = dto.localPrice;
    product.smartcardNumber = dto.smartcardNumber || '';
    product.smartcardName = dto.smartcardName;
    product.isPrimarySc = dto.isPrimarySc || false;
    product.isSecondarySc = dto.isSecondarySc || false;
    product.isXtraView = dto.isXtraView || false;
    product.viewingDaysLeft = dto.viewingDaysLeft;
    product.agreementType = dto.agreementType;
    product.agreementType = dto.agreementType === "Employee OTT"
      ? 'OTT'
      : dto.agreementType;
    product.model = dto.model;
    product.modelDescription = dto.modelDescription;
    product.addons = dto.addons || [];
    product.flexProducts = dto.flexProducts || [];
    product.hasServices = dto.hasServices || false;
    product.parentId = dto.parentId;
    product.isInsured = dto.isInsured;
    product.isHighestPackage = dto.isHighestPackage;
    product.isPendingChangePackageSchedule = dto.isPendingChangePackageSchedule;
    product.scheduleId = dto.scheduleId;
    if (dto.agreementId !== undefined) {
      product.agreementId = dto.agreementId;
    }
    if (dto.productId !== undefined) {
      product.productId = dto.productId;
    }
    return product;
  }

  static FromSubscriptionDTOArray(subscriptions: GetSubscriptionDTO) {
    return [
      ...subscriptions.subscriptions.map((subscription) => {
        if (!subscription?.services || !subscription?.services.length) {
          return Product.FromSubscriptionDTO({ ...subscription }, new Product());
        }

        const augmentedService = subscription.services
          ?.filter(
            (service: ServicesDTO) =>
              service.type !== 'Access' &&
              service.type !== 'Bundle' &&
              service.type !== 'Product Installation'
            // FIXME: Not sure how to represent product installations & bundles at this stage.
          )
          .reduce(
            (previous: { product: Partial<Product>; addons: Partial<Product>[]; flexProducts: Partial<Product>[] }, service: Partial<Product>) => {
              if (service.status === 'Pending Upgrade') {
                // FIXME: ignore pending upgrades for now, as there is no clear way of solving this.
                return previous;
              }

              if (service.type === 'Principal' || service.type === 'Short Term Subscription' || service.type === 'SecurityLifestyle') {
                previous.addons = previous.addons?.map((addon) => ({ ...addon, parentId: service.productId }));
                return {
                  ...previous,
                  product: {
                    ...service,
                    actions: subscription.actions,
                    active: service.status !== 'Disconnected',
                    isActive:
                      service.status !== 'Disconnected' &&
                      // service.status !== 'Suspended' &&
                      service.status !== 'Request Disconnect' &&
                      service.status !== 'Disconnect Pending Reconnect',
                    smartcardName: subscription.smartcardName,
                    serialNumber: subscription.serialNumber,
                    smartcardNumber: subscription.smartcardNumber,
                    viewingDaysLeft: subscription.viewingDaysLeft,
                    isPrimarySc: subscription.isPrimarySc,
                    isSecondarySc: subscription.isSecondarySc,
                    isXtraView: subscription.isXtraView,
                    agreementType: subscription.agreementType === 'Employee OTT'
                      ? 'OTT'
                      : subscription.agreementType,
                    agreementId: subscription.agreementId,
                    productId: service.productId,
                    model: subscription.model,
                    modelDescription: subscription.modelDescription,
                    hasServices: true,
                    serviceDescription: service.description || '',
                    isInsured: subscription.isInsured,
                    isPendingChangePackageSchedule: subscription.isPendingChangePackageSchedule,
                    scheduleId: subscription.scheduleId,
                  },
                };
              }

              if (service.type === `${VasCodesType.FLEX}` && previous.flexProducts.length < 1) {
                previous.addons = previous.addons?.map((addon) => ({ ...addon, parentId: service.productId }));
                return {
                  ...previous,
                  flexProducts: [
                    ...previous.flexProducts,
                    {
                      ...service,
                      active: service.status !== 'Disconnected',
                      isActive:
                        service.status !== 'Disconnected' &&
                        service.status !== 'Request Disconnect' &&
                        service.status !== 'Disconnect Pending Reconnect',
                      productId: service.productId,
                      smartcardName: subscription.smartcardName,
                      serialNumber: subscription.serialNumber,
                      smartcardNumber: subscription.smartcardNumber,
                      viewingDaysLeft: subscription.viewingDaysLeft,
                      isPrimarySc: subscription.isPrimarySc,
                      isSecondarySc: subscription.isSecondarySc,
                      isXtraView: subscription.isXtraView,
                      agreementType: subscription.agreementType,
                      agreementId: subscription.agreementId,
                      model: subscription.model,
                      modelDescription: subscription.modelDescription,
                      hasServices: true,
                      parentId: previous.product.productId,
                      serviceDescription: service.description || '',
                      isInsured: subscription.isInsured,
                      isPendingChangePackageSchedule: subscription.isPendingChangePackageSchedule,
                      scheduleId: subscription.scheduleId,
                    },
                  ],
                  product: {
                    ...service,
                    serviceName: 'SELECT',
                    actions: subscription.actions,
                    active: true,
                    isActive: true,

                    smartcardName: subscription.smartcardName,
                    serialNumber: subscription.serialNumber,
                    smartcardNumber: subscription.smartcardNumber,
                    viewingDaysLeft: subscription.viewingDaysLeft,
                    isPrimarySc: subscription.isPrimarySc,
                    isSecondarySc: subscription.isSecondarySc,
                    isXtraView: subscription.isXtraView,
                    agreementType: subscription.agreementType,
                    agreementId: subscription.agreementId,
                    productId: service.productId,
                    model: subscription.model,
                    modelDescription: subscription.modelDescription,
                    hasServices: true,
                    serviceDescription: service.description || '',
                    isInsured: subscription.isInsured,
                    isPendingChangePackageSchedule: subscription.isPendingChangePackageSchedule,
                    scheduleId: subscription.scheduleId,
                  },
                };
              }

              if (service.type === `${VasCodesType.FLEX}`) {
                return  {
                  product: previous.product,
                  addons: previous.addons,
                  flexProducts: [
                    ...previous.flexProducts,
                    {
                      ...service,
                      active: service.status !== 'Disconnected',
                      isActive:
                        service.status !== 'Disconnected' &&
                        service.status !== 'Request Disconnect' &&
                        service.status !== 'Disconnect Pending Reconnect',
                      productId: service.productId,
                      smartcardName: subscription.smartcardName,
                      serialNumber: subscription.serialNumber,
                      smartcardNumber: subscription.smartcardNumber,
                      viewingDaysLeft: subscription.viewingDaysLeft,
                      isPrimarySc: subscription.isPrimarySc,
                      isSecondarySc: subscription.isSecondarySc,
                      isXtraView: subscription.isXtraView,
                      agreementType: subscription.agreementType,
                      agreementId: subscription.agreementId,
                      model: subscription.model,
                      modelDescription: subscription.modelDescription,
                      hasServices: true,
                      parentId: previous.product.productId,
                      serviceDescription: service.description || '',
                      isInsured: subscription.isInsured,
                      isPendingChangePackageSchedule: subscription.isPendingChangePackageSchedule,
                      scheduleId: subscription.scheduleId,
                    },
                  ],
                };
              }

              return {
                product: previous.product,
                flexProducts: previous.flexProducts,
                addons: [
                  ...previous.addons,
                  {
                    ...service,
                    active: service.status !== 'Disconnected',
                    isActive:
                      service.status !== 'Disconnected' &&
                      // service.status !== 'Suspended' &&
                      service.status !== 'Request Disconnect' &&
                      service.status !== 'Disconnect Pending Reconnect',
                    productId: service.productId,
                    smartcardName: subscription.smartcardName,
                    serialNumber: subscription.serialNumber,
                    smartcardNumber: subscription.smartcardNumber,
                    viewingDaysLeft: subscription.viewingDaysLeft,
                    isPrimarySc: subscription.isPrimarySc,
                    isSecondarySc: subscription.isSecondarySc,
                    isXtraView: subscription.isXtraView,
                    agreementType: subscription.agreementType,
                    agreementId: subscription.agreementId,
                    model: subscription.model,
                    modelDescription: subscription.modelDescription,
                    hasServices: true,
                    parentId: previous.product.productId,
                    serviceDescription: service.description || '',
                    isInsured: subscription.isInsured,
                    isPendingChangePackageSchedule: subscription.isPendingChangePackageSchedule,
                    scheduleId: subscription.scheduleId,
                  },
                ],
              };
            },
            { product: {}, addons: [], flexProducts: [] }
          ) as { product: Partial<SubscriptionDTO> & ServicesDTO; addons: ServicesDTO[]; flexProducts: ServicesDTO[] };

        return Product.FromSubscriptionDTO(
          { ...augmentedService.product, addons: augmentedService.addons, flexProducts: augmentedService.flexProducts },
          new Product()
        );
      }),
    ];
  }

  static MapServiceToDevice(subscriptions: SubscriptionDTO[]) {
    return [
      ...subscriptions.map((_subscription: SubscriptionDTO) => {
        if (!_subscription.services) {
          return _subscription;
        }
        const output = _subscription as Partial<Hardware>;
        output.serviceName = _subscription.services.find(
          (_service: ServicesDTO) => _service.type === 'Principal'
        )?.serviceName;
        return output;
      }),
    ] as Hardware[];
  }
}